import React from 'react';

import ConfColorPicker from './ConfColorPicker';
import ConfSizePicker from './ConfSizePicker';
import ConfVariantPicker from './ConfVariantPicker';
import ConfExtendableSlider from './ConfExtendableSlider';
import ConfVariantToggler from './ConfVariantToggler';
import AddonPicker from './AddonPicker';

import productService from '../../services/products';
import tacHelpers from '../../state/tac/tacHelpers';
import { unique } from '../../util/array';
import updateItem from '../../state/tac/tacReducer/updateItem';
import constants from '../../settings/constants';
import { round } from '../../util/round';
import { range } from './range';
import { isSection } from '../../services/products';
import { translate } from '../../services/L10n';
import styles from './ConfMenu.module.less';
import _ from 'lodash';
import { getUserAgent, isMobile } from '../../util/userAgent';
import getItemConfig from '../../scene/util/getItemConfig';
import { isFloorStanding, isWallMounted } from '../../services/products/models';
import { ITEMS } from '../../constants';
import { t } from '../../translations';

const selections = {
  section: {
    key: ITEMS.SECTION,
    header: 'POPUP_MENU_HEADER_SECTION',
    order: 1,
  },
  frame: { key: ITEMS.FRAME, header: 'POPUP_MENU_HEADER_FRAME', order: 2 },
  basket: { key: ITEMS.BASKET, header: 'POPUP_MENU_HEADER_BASKETS', order: 3 },
  insert: { key: 'insert', header: 'POPUP_MENU_HEADER_INSERTS', order: 4 },
};

function rotationAllowed(currentItem, rotatedItem, tac) {
  const newItem = tacHelpers.getSwitchableItem(
    currentItem,
    {
      ...rotatedItem,
      value: rotatedItem.filter.variant,
      type: rotatedItem.filter.type,
    },
    {
      switchingProp: 'variant',
    }
  );

  const parent = tacHelpers.getParent(tac, currentItem);
  const rotationIsValid = updateItem(tac, newItem, parent, {
    moveOthers: true,
  });
  return rotationIsValid;
}

function getChildOptions(item) {
  const optionItems = item.items
    .filter(
      item =>
        productService.shouldHaveColorSelector(item) &&
        !productService.isMultiParentProduct(item)
    )
    .map(childItem =>
      productService.getSwappables(childItem, {
        depth: childItem.filter.depth,
        width: childItem.filter.width,
      })
    )
    .filter(childSwappables => childSwappables && childSwappables.length > 1)
    .flat()
    .map(swappable => ({ ...swappable, id: item.id }));

  return { optionItems };
}

function multiSelector(currentItem, tac, type, filterProp) {
  let optionItems = [];
  let defaultProduct;

  const usedProductsOfType = currentItem.items
    ? tacHelpers.getAllItems(currentItem.items).filter(item => {
        return productService.isType(item, type);
      })
    : [];

  const usedPeers = usedProductsOfType
    .map(product => product.filter[filterProp])
    .filter(unique);

  // if there are no items of {type}, then there's nothing to change
  // leave optionItems empty
  if (usedProductsOfType.length > 0) {
    const optionToChangeIsValid = usedProductsOfType.every(product => {
      /*
      Castors is a swappable for pads, but it won't fit on all of the same parent (Lex shelving units).
      Therefore we need to validate that all swappables actually have a valid slot on the parent,
      given that the parent were to be cleared of all children with the same type.
      */
      const currentItemAndDescendants = tacHelpers.getAllItems([currentItem]);
      const section = currentItemAndDescendants.find(isSection);
      if (section) {
        Array.prototype.push.apply(
          currentItemAndDescendants,
          tacHelpers.range.getSlotSources(section, tac)
        );
      }

      const itemsOfType = currentItemAndDescendants.filter(item =>
        productService.isType(item, type)
      );
      const filteredTac = tacHelpers.filterTac(
        { items: [currentItem] },
        itemsOfType.map(item => item.itemid)
      );

      const swappables = productService.getSwappables(product, {
        depth: product.filter.depth,
        width: product.filter.width,
      });
      if (!productService.isType(product, 'leg') && swappables.length > 1) {
        return true;
      }

      return swappables.every(swappable => {
        return tacHelpers.getOpenSlots(filteredTac, swappable, {
          noVariants: true,
        }).length;
      });
    });
    if (optionToChangeIsValid) {
      defaultProduct = usedProductsOfType[0];
      optionItems = productService
        .getSwappables(defaultProduct, {
          depth: defaultProduct.filter.depth,
          width: defaultProduct.filter.width,
        })
        .map(defaultProduct => defaultProduct.filter)
        .map(peer => ({
          ...currentItem,
          ...{
            filter: {
              [filterProp]: peer[filterProp],
              type: peer.type,
            },
          },
        }));
    }
  }

  if (optionItems.length > 1) {
    const selected = defaultProduct
      ? {
          ...currentItem,
          id: 'dont select',
          hasDivergentItems: usedPeers.length > 1,
          filter: defaultProduct.filter,
        }
      : currentItem;

    return { selected, optionItems };
  }
}

function colorSelector(item, tac) {
  const optionItems = [];
  let items = [];

  const defaultFilters = {
    width: item.filter.width,
    depth: item.filter.depth,
    ...(tacHelpers.getTopAncestor(tac, item).itemid === item.itemid && {
      ...(isWallMounted(item) && item.y > 0 && { wall: true }),
      ...(isFloorStanding(item) && item.y === 0 && { floor: true }),
    }),
  };

  if (productService.shouldHaveColorSelector(item)) {
    if (productService.isFrame(item)) {
      items = productService.getSwappables(item, {
        ...defaultFilters,
        height: item.filter.height,
        variant: item.filter.variant,
      });

      const swappablesChildren = item.items
        .filter(
          childItem =>
            productService.isType(childItem, [
              ITEMS.SHELVING_UNIT,
              ITEMS.FRAME,
            ]) && item.id !== childItem.id
        )
        .flatMap(childItem =>
          productService.getSwappables(childItem, {
            ...defaultFilters,
            height: childItem.filter.height,
            variant: childItem.filter.variant,
          })
        );

      const canSwapAllChildren =
        swappablesChildren.length === 0 ||
        items.every(swappableItem =>
          swappablesChildren.find(
            child => child.filter.color === swappableItem.filter.color
          )
        );

      if (items.length > 1 && !canSwapAllChildren) {
        items = [];
      } else {
        item.hasDivergentItems = item.items
          .filter(childItem => productService.isFrame(childItem))
          .some(childItem => childItem.filter.color !== item.filter.color);
      }
    } else if (productService.isSection(item)) {
      items = productService.getSwappables(item, {
        ...defaultFilters,
        height: item.filter.height,
      });
      item.hasDivergentItems = tacHelpers.range.isMultiColouredSection(
        item,
        tac
      );
    } else if (productService.isUpright(item)) {
      items = productService.getSwappables(item, {
        ...defaultFilters,
        height: item.filter.height,
      });
    } else if (productService.isTrolley(item)) {
      items = productService.getSwappables(item, {
        ...defaultFilters,
        variant: item.filter.variant,
      });
    } else {
      items = productService.getSwappables(item, {
        ...defaultFilters,
      });
    }
    if (items.length > 1) {
      optionItems.push({
        selection: (() => {
          if (productService.isFrame(item)) {
            return selections.frame.key;
          } else if (productService.isSection(item)) {
            return selections.section.key;
          }

          return selections.insert.key;
        })(),
        component: (
          <ConfColorPicker
            key="color"
            showHeadline={productService.shouldHaveHeadlineForSelectors(item)}
            sortOrder={(() => {
              if (productService.isSection(item)) {
                return ITEMS.SECTION;
              } else if (
                productService.isType([
                  ITEMS.SHELF,
                  ITEMS.METAL_SHELF,
                  ITEMS.WIRE_SHELF,
                ])
              ) {
                return 'insert';
              }
              return ITEMS.ITEM;
            })()}
            item={item}
            selected={item}
            items={items}
            targetType={item.filter.type}
          />
        ),
      });
    }
  }

  if (productService.shouldHaveChildColorSelector(item)) {
    const selector = getChildOptions(item);

    if (selector?.optionItems.length) {
      optionItems.push({
        selection: selections.insert.key,
        component: (
          <ConfColorPicker
            key="color"
            showHeadline={productService.shouldHaveHeadlineForSelectors(item)}
            sortOrder={'insert'}
            item={item}
            selected={range.getSelectedChild(
              item,
              selector.optionItems,
              'color'
            )}
            items={selector.optionItems}
          />
        ),
      });
    }
  }

  return optionItems;
}

function extendableSlider(item, tac) {
  if (!productService.isExtendable(item)) {
    return;
  }

  const limits = tacHelpers.extendableLimits(tac, item);

  if (!limits) {
    return;
  }

  const config = getItemConfig(item);
  const mountOffset = config?.mountOffset || 0;

  //Round to cm and remove possible mounting width
  limits.displayedMin = round(limits.min - mountOffset * 2, 10);
  limits.displayedMax = round(limits.max - mountOffset * 2, 10);

  const topAncestor = tacHelpers.getTopAncestor(tac, item);

  return (
    limits && {
      selection: productService.isSection(item)
        ? selections.section.key
        : selections.insert.key,
      component: (
        <ConfExtendableSlider
          key="slider"
          limits={limits}
          item={item}
          topAncestor={topAncestor}
          tac={tac}
          mountOffset={mountOffset}
        />
      ),
    }
  );
}

function sizeSelector(section) {
  if (!productService.isSection(section)) {
    return;
  }

  const dimension = range.getChangeDimension(section);

  const otherDimensions = ['width', 'height', 'depth'].filter(
    dim => dim !== dimension
  );

  const filter = { color: section.filter.color, switchable: true };

  otherDimensions.forEach(dim => (filter[dim] = section.filter[dim]));

  const sections = productService.getSwappables(section, filter);

  let validSections = sections;
  if (section.items && section.items.length !== 0) {
    validSections = sections.filter(function (cand) {
      if (cand.filter[dimension] === section.filter[dimension]) {
        // this is the original section so it obviously works
        return true;
      }
      if (cand.logic.extendable) {
        // we allow switching to extendable even if stuff wont fit
        return true;
      }

      const dimensionAxisMap = {
        width: 'x',
        height: 'y',
        depth: 'z',
      };
      // detect articles that can't be replaced
      return section.items.every(function (part) {
        const offset = productService.getSectionOffset(part);
        const partFilter = {
          [dimension]:
            cand.filter[dimension] - offset[dimensionAxisMap[dimension]] * 2,
          color: part.filter.color,
        };
        otherDimensions.forEach(dim => (partFilter[dim] = part.filter[dim]));

        return productService.getSwappables(part, partFilter).length > 0;
      });
    });
  }

  if (sections.length > 1) {
    return {
      selection: selections.section.key,
      component: (
        <ConfSizePicker
          key={dimension}
          dimension={dimension}
          selected={section}
          items={sections}
          disabled={validSections.length < 2}
        />
      ),
    };
  }
}

function variantSelector(item, tac) {
  const variantSelectors = [];

  if (productService.isTrolley(item)) {
    const trolleys = productService.getSwappables(item, {
      color: item.filter.color,
    });

    if (trolleys.length > 1) {
      return {
        selection: selections.insert.key,
        component: (
          <ConfVariantToggler
            key="trolleyVariant"
            selected={item}
            items={trolleys}
          />
        ),
      };
    }
  } else if (
    productService.isType(item, ITEMS.BASKET) &&
    !productService.shouldHaveColorSelector(item)
  ) {
    const baskets = productService.getSwappables(item, {
      depth: item.filter.depth,
      width: item.filter.width,
    });

    if (baskets.length > 1) {
      variantSelectors.push({
        selection: selections.basket.key,
        component: (
          <ConfVariantPicker
            key="variant"
            showHeadline={productService.shouldHaveHeadlineForSelectors(item)}
            selected={item}
            items={baskets}
          />
        ),
      });
    }
  } else if (
    productService.isType(item, [
      ITEMS.SHELF,
      ITEMS.METAL_SHELF,
      ITEMS.WIRE_SHELF,
    ])
  ) {
    const shelves = productService.getSwappablesOfType(
      [ITEMS.SHELF, ITEMS.METAL_SHELF, ITEMS.WIRE_SHELF],
      {
        depth: item.filter.depth,
        width: item.filter.width,
        color: item.filter.color,
      }
    );

    if (shelves.length > 1) {
      variantSelectors.push({
        selection: selections.insert.key,
        component: (
          <ConfVariantPicker
            key="variant"
            showHeadline={productService.shouldHaveHeadlineForSelectors(item)}
            selected={item}
            items={shelves}
            useColorSpecificIcon={true}
          />
        ),
      });
    }
  } else if (productService.isType(item, [ITEMS.FRAME, ITEMS.SHELVING_UNIT])) {
    const legSelector = multiSelector(item, tac, 'leg', 'variant');
    const basketSelector = multiSelector(item, tac, ITEMS.BASKET, 'variant');
    variantSelectors.push(
      legSelector && {
        selection: selections.frame.key,
        component: (
          <ConfVariantToggler
            key="legVariant"
            selected={legSelector.selected}
            items={legSelector.optionItems}
          />
        ),
      },
      basketSelector && {
        selection: selections.basket.key,
        component: (
          <ConfVariantPicker
            key="basketVariant"
            showHeadline={productService.shouldHaveHeadlineForSelectors(item)}
            selected={basketSelector.selected}
            items={basketSelector.optionItems}
          />
        ),
      }
    );
  }
  if (productService.isType(item, ITEMS.SHELVING_UNIT)) {
    if (item.y > constants.PAD_HEIGHT) {
      // item is not on floor
      return variantSelectors;
    }
    if (
      item.items &&
      item.items.some(
        child =>
          !productService.isType(child, [
            ITEMS.SHELVING_UNIT,
            ITEMS.TOP_SHELF,
            'leg',
          ])
      )
    ) {
      /*
      item has something other than other shelving unit/top shelf/legs as a child,
      which means we can't rotate it
      */
      return variantSelectors;
    }
    const shelvingUnits = productService.getSwappables(item, {
      height: item.filter.height,
      variant: item.filter.variant,
    });

    if (shelvingUnits.length > 1) {
      const otherShelvingUnit = shelvingUnits.find(
        other => other.id !== item.id
      );

      const disabled = !rotationAllowed(item, otherShelvingUnit, tac);

      variantSelectors.push({
        selection: selections.frame.key,
        component: (
          <ConfVariantToggler
            key="width"
            selected={item}
            items={shelvingUnits}
            disabled={disabled}
            moveOthers={true}
          />
        ),
      });
    }
  }
  return variantSelectors.length > 0 ? variantSelectors : undefined;
}

// Note: Not in any way related to BROR addon shelves
function addonSelector(item, tac) {
  if (productService.isType(item, ITEMS.FRAME)) {
    const covers = productService.getFilteredItems(
      item => {
        return productService.isType(item, 'cover');
      },
      {
        width: item.width,
        depth: item.depth,
        height: item.height,
      }
    );

    if (covers.length === 1) {
      const addableCover = covers[0];
      const slots = covers.length && tacHelpers.getOpenSlots(tac, addableCover);
      const currentItemSlot =
        slots && slots.find(slot => slot.parent.itemid === item.itemid);

      const currentCover =
        item.items &&
        item.items.find(item => covers.some(cover => cover.id === item.id));

      if (currentCover || currentItemSlot) {
        return {
          selection: selections.frame.key,
          component: (
            <AddonPicker
              key="addon"
              parent={item}
              items={[addableCover]}
              addonType="cover"
              slot={currentItemSlot}
              selected={currentCover}
            />
          ),
        };
      }
    }
  }
}

function addDivider(selectionOptions) {
  const userAgent = getUserAgent();
  if (
    userAgent.orientation === 'portrait' &&
    userAgent.deviceType === 'mobile'
  ) {
    return selectionOptions.reduce((options, current, index) => {
      if (index > 0 && index < selectionOptions.length) {
        options.push(<div key={index} className={styles.divider} />);
      }
      options.push(current);
      return options;
    }, []);
  }

  return selectionOptions;
}

function generateSelectionOptions(selectionOptions) {
  const addon = selectionOptions.find(option => option.key === 'addon');
  const legVariant = selectionOptions.find(
    option => option.key === 'legVariant'
  );

  if (addon && legVariant) {
    selectionOptions.unshift(
      <div
        key={addon.key + legVariant.key}
        className={styles.joinedSelectionOptions}
      >
        {[legVariant, addon]}
      </div>
    );
    return addDivider(
      selectionOptions.filter(
        option => option.key !== 'addon' && option.key !== 'legVariant'
      )
    );
  }

  return addDivider(selectionOptions);
}

function shouldSplitSelections(options) {
  let conflictingSelections = 0;
  const conflictingSelectionKeys = ['color', 'basketVariant'];

  return options.some(option => {
    conflictingSelections += conflictingSelectionKeys.includes(
      option.component.key
    )
      ? 1
      : 0;
    return conflictingSelections > 1;
  });
}

function getOptionsSelections(options) {
  return Object.entries(
    options.reduce((optionSelections, option) => {
      if (optionSelections[option.selection]) {
        optionSelections[option.selection].push(option.component);
      } else {
        optionSelections[option.selection] = [option.component];
      }
      return optionSelections;
    }, {})
  ).map(optionSelection => (
    <div key={optionSelection[0]} className={styles.selectionContainer}>
      {selections[optionSelection[0]] && (
        <b className={styles.selectionHeader}>
          {translate(t[selections[optionSelection[0]].header])}
        </b>
      )}
      <div className={styles.selectionOptions}>
        {generateSelectionOptions(optionSelection[1])}
      </div>
    </div>
  ));
}

function wrapSelections(options) {
  const userAgent = getUserAgent();
  const wrappingOptionKeys = ['slider', 'width', 'color'];
  const sizeSelector = options.find(option => option.component.key === 'width');
  const slider = options.find(option => option.component.key === 'slider');
  const colorSelector = options.find(
    option => option.component.key === 'color'
  );

  if (
    (sizeSelector || colorSelector) &&
    slider &&
    isMobile() &&
    userAgent.orientation === 'portrait'
  ) {
    const wrappedComponents = [];
    const separateComponents = [];
    options.forEach(option => {
      if (wrappingOptionKeys.includes(option.component.key)) {
        wrappedComponents.push(
          React.cloneElement(option.component, {
            wrapped: true,
          })
        );
      } else {
        separateComponents.push(option.component);
      }
    });

    wrappedComponents.sort((a, b) => {
      return (
        wrappingOptionKeys.indexOf(a.key) - wrappingOptionKeys.indexOf(b.key)
      );
    });

    return [
      <div className={styles.selectionOptions} key={wrappedComponents}>
        {wrappedComponents}
      </div>,
      separateComponents,
    ];
  }

  return null;
}

export function generateOptions(item, tac) {
  const options = [
    variantSelector(item, tac),
    addonSelector(item, tac),
    ...colorSelector(item, tac),
    sizeSelector(item),
    extendableSlider(item, tac),
  ]
    .flat()
    .filter(Boolean)
    .sort((a, b) =>
      _.clamp(
        selections[a.selection].order - selections[b.selection].order,
        -1,
        1
      )
    );

  if (shouldSplitSelections(options)) {
    return getOptionsSelections(options);
  }

  return wrapSelections(options) || options.map(option => option.component);
}
